import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import ChatMessages from 'pages/Chat/ChatMessages';
import './mapSpotlightChat.css';

const ICONS = {
  chevron: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 5.15c.22 0 .44.08.6.25L8 8.8l3.4-3.4a.85.85 0 1 1 1.2 1.2L8 11.2 3.4 6.6A.85.85 0 0 1 4 5.15Z" fill="currentColor" />
    </svg>
  ),
  select: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.5 3.5h4v1.2H3.7v2.8H2.5V3.5Zm7 0h4V7.5h-1.2V4.7H9.5V3.5Zm-7 5h1.2v2.8h2.8V12.5h-4V8.5Zm10.3 0H13.5v4h-4v-1.2h2.8V8.5Z" fill="currentColor" />
    </svg>
  ),
  send: (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" aria-hidden="true">
      <path fill="currentColor" fillRule="evenodd" d="M7.4 1.899a.85.85 0 0 1 1.201 0l4.5 4.5A.85.85 0 1 1 11.9 7.6L8.85 4.552V13.5a.85.85 0 0 1-1.7 0V4.552L4.101 7.601A.85.85 0 1 1 2.9 6.399z" clipRule="evenodd" />
    </svg>
  ),
};

const formatZone = (bbox) => {
  if (!bbox) return '';
  const fmt = (n) => Number(n).toFixed(2);
  return `${fmt(bbox.minLat)}–${fmt(bbox.maxLat)}°N, ${fmt(bbox.minLng)}–${fmt(bbox.maxLng)}°E`;
};

export default function MapSpotlightChat({
  open,
  onOpenChange,
  messages = [],
  text = '',
  onTextChange,
  onSend,
  onSuggestionSelect,
  suggestions = [],
  loading = false,
  typing = false,
  error = '',
  onRetry,
  authenticated = false,
  zoneBbox = null,
  onClearZone,
  selectMode = false,
  onToggleSelect,
  fullChatHref = '',
}) {
  const [panelState, setPanelState] = useState(open ? 'open' : 'closed');
  const [focused, setFocused] = useState(false);
  const [composing, setComposing] = useState(false);
  const editorRef = useRef(null);
  const logRef = useRef(null);

  useEffect(() => {
    if (open) {
      if (panelState === 'closed' || panelState === 'closing') setPanelState('opening');
      return undefined;
    }
    if (panelState === 'open' || panelState === 'opening') {
      setPanelState('closing');
      const timer = window.setTimeout(() => setPanelState('closed'), 400);
      return () => window.clearTimeout(timer);
    }
    return undefined;
  }, [open, panelState]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (selectMode) {
        onToggleSelect?.(false);
        return;
      }
      if (open) onOpenChange?.(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selectMode, onOpenChange, onToggleSelect]);

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    el.style.height = '22px';
    el.style.height = `${Math.min(120, Math.max(22, el.scrollHeight))}px`;
  }, [text, open]);

  useEffect(() => {
    if (!open) return;
    const el = logRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, typing, open, loading]);

  const visible = panelState !== 'closed';
  const canSend = Boolean(text.trim()) && !loading;
  const placeholder = zoneBbox
    ? 'Ask about this region…'
    : 'Select a zone, then ask about the region…';

  const sendNow = () => {
    if (!canSend) return;
    onOpenChange?.(true);
    onSend?.(text.trim());
  };

  return (
    <div className="map-spot">
      <div className="map-spot__dock">
        {visible ? (
          <div
            className="map-spot__shell"
            data-state={panelState}
            role="region"
            aria-label="Map assistant"
            onAnimationEnd={(e) => {
              if (e.target !== e.currentTarget) return;
              if (panelState === 'opening') setPanelState('open');
              if (panelState === 'closing') setPanelState('closed');
            }}
          >
            <div className="map-spot__glass">
              <div className="map-spot__border" />
            </div>
            <div className="map-spot__body">
              <header className="map-spot__header">
                <button type="button" className="map-spot__identity" onClick={() => onOpenChange?.(false)}>
                  <span className="map-spot__avatar">
                    <img src="/chat-logo.png" alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                  </span>
                  <span className="map-spot__titles">
                    <h2 className="map-spot__name">CoralFuture AI</h2>
                    <p className="map-spot__sub">
                      {zoneBbox ? 'Zone geo-RAG · grounded on selection' : 'Select a zone to analyze a region'}
                    </p>
                  </span>
                  {ICONS.chevron}
                </button>
                <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                  {fullChatHref ? (
                    <Link
                      to={fullChatHref}
                      className="map-spot__icon-btn"
                      aria-label="Open full chat"
                      title="Open full chat"
                      style={{ textDecoration: 'none', fontSize: 11, width: 'auto', padding: '0 8px', color: 'var(--spot-muted)' }}
                    >
                      Full
                    </Link>
                  ) : null}
                  <button type="button" className="map-spot__icon-btn" aria-label="Collapse" onClick={() => onOpenChange?.(false)}>
                    {ICONS.chevron}
                  </button>
                </div>
              </header>

              <div className="map-spot__log" ref={logRef} role="log" aria-live="polite">
                {!messages.length && !loading && !typing ? (
                  <div className="map-spot__empty">
                    <div className="map-spot__notice">
                      Draw a rectangle on the map, then ask about bleaching, SST, projects, or habitat in that zone.
                    </div>
                    {suggestions.length ? (
                      <div className="map-spot__chips map-spot__chips--panel">
                        {suggestions.slice(0, 3).map((chip) => (
                          <button
                            key={chip}
                            type="button"
                            className="map-spot__chip"
                            onClick={() => onSuggestionSelect?.(chip)}
                          >
                            {chip}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {messages.length ? <ChatMessages messages={messages} /> : null}
                {typing || loading ? (
                  <div className="map-spot__typing" aria-label="Thinking">
                    <span /><span /><span />
                  </div>
                ) : null}
                {error ? (
                  <div className="map-spot__error">
                    {error}
                    {onRetry ? (
                      <button type="button" onClick={() => onRetry()}>Retry</button>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        {!open && suggestions.length ? (
          <div className="map-spot__chips">
            {suggestions.slice(0, 3).map((chip) => (
              <button
                key={chip}
                type="button"
                className="map-spot__chip"
                onClick={() => {
                  onOpenChange?.(true);
                  onSuggestionSelect?.(chip);
                }}
              >
                {chip}
              </button>
            ))}
          </div>
        ) : null}

        <div className="map-spot__composer">
          <div className={`map-spot__pill${focused ? ' is-focus' : ''}`}>
            {(zoneBbox || selectMode) ? (
              <div className="map-spot__zone-row">
                {zoneBbox ? (
                  <span className="map-spot__zone-chip">
                    Zone {formatZone(zoneBbox)}
                    <button type="button" aria-label="Clear zone" onClick={() => onClearZone?.()}>×</button>
                  </span>
                ) : (
                  <span className="map-spot__zone-chip">Drag a rectangle on the map…</span>
                )}
              </div>
            ) : null}
            <div className="map-spot__row">
              <div className="map-spot__field">
                <textarea
                  ref={editorRef}
                  className="map-spot__input"
                  rows={1}
                  value={text}
                  disabled={loading}
                  aria-label={placeholder}
                  onFocus={() => {
                    setFocused(true);
                    onOpenChange?.(true);
                  }}
                  onBlur={() => setFocused(false)}
                  onChange={(e) => onTextChange?.(e.target.value)}
                  onCompositionStart={() => setComposing(true)}
                  onCompositionEnd={() => setComposing(false)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !composing) {
                      e.preventDefault();
                      sendNow();
                    }
                  }}
                />
                {!text ? <div className="map-spot__ph">{placeholder}</div> : null}
              </div>
              <div className="map-spot__tools">
                <button
                  type="button"
                  className={`map-spot__tool${selectMode ? ' is-active' : ''}`}
                  aria-label="Select zone"
                  title="Select zone"
                  onClick={() => onToggleSelect?.(!selectMode)}
                >
                  {ICONS.select}
                </button>
                <button
                  type="button"
                  className={`map-spot__send${canSend ? ' is-on' : ''}`}
                  aria-label="Send"
                  disabled={!canSend}
                  onClick={sendNow}
                >
                  {ICONS.send}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
